import type { TimeEntry, Project, ISODateString, PeriodMonth } from '../types/timesheet.types';
import { TimesheetStatus, ProjectStatus } from '../types/timesheet.types';
import { VALIDATION, PROJECT_END_WARNING_DAYS } from './constants';
import { parseISODate, isDateInMonth } from './dateUtils';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

const OK: ValidationResult = { valid: true, errors: [], warnings: [] };

function fail(errors: string[]): ValidationResult {
  return { valid: false, errors, warnings: [] };
}

// ─── Cell-level ───────────────────────────────────────────────────────────────

export function validateHoursInput(raw: string): ValidationResult {
  if (raw === '' || raw === null) return OK;

  // Parse HH.MM format: split on first dot, treat right side as minutes
  const dot = raw.indexOf('.');
  const h = parseInt(dot === -1 ? raw : raw.slice(0, dot), 10);
  const mStr = dot === -1 ? '00' : raw.slice(dot + 1).padEnd(2, '0').slice(0, 2);
  const m = parseInt(mStr, 10);

  if (isNaN(h) || isNaN(m)) return fail(['Enter hours in HH.MM format (e.g. 2.30)']);
  if (h < 0 || m < 0) return fail(['Cannot be negative']);
  if (![0, 15, 30, 45].includes(m)) return fail(['Minutes must be 00, 15, 30, or 45']);

  const decimal = h + m / 60;
  if (decimal > VALIDATION.MAX_DAILY_HOURS) {
    return fail([`Daily maximum is ${VALIDATION.MAX_DAILY_HOURS}h`]);
  }

  const warnings: string[] =
    decimal > VALIDATION.OOM_WARNING_THRESHOLD
      ? [`>${VALIDATION.OOM_WARNING_THRESHOLD}h on this day — verify it's correct`]
      : [];

  return { valid: true, errors: [], warnings };
}

// ─── Project validation ───────────────────────────────────────────────────────

export function validateProjectForEntry(
  projectCode: string,
  entryDate: ISODateString,
  userId: string,
  projects: readonly Project[]
): ValidationResult {
  const project = projects.find(p => p.projectCode === projectCode);
  if (!project) return fail([`Project '${projectCode}' not found`]);

  const errors: string[] = [];
  const warnings: string[] = [];

  if (project.status !== ProjectStatus.Active) errors.push(`'${projectCode}' is closed`);

  const date = parseISODate(entryDate);
  const start = parseISODate(project.startDate);
  if (date < start) errors.push(`Before project start (${project.startDate})`);

  if (project.endDate) {
    const end = parseISODate(project.endDate);
    if (date > end) {
      errors.push(`After project end (${project.endDate})`);
    } else {
      const daysLeft = Math.ceil((end.getTime() - date.getTime()) / 86_400_000);
      if (daysLeft <= PROJECT_END_WARNING_DAYS) {
        warnings.push(`'${projectCode}' ends in ${daysLeft} days`);
      }
    }
  }

  if (!project.assignedTeamEmails.includes(userId)) {
    errors.push(`You are not assigned to '${projectCode}'`);
  }

  return { valid: errors.length === 0, errors, warnings };
}

// ─── Month submission ─────────────────────────────────────────────────────────

export function validateMonthSubmission(
  entries: readonly TimeEntry[],
  periodMonth: PeriodMonth,
  bankHolidayDates: ReadonlySet<ISODateString>,
  allWorkingDays: readonly ISODateString[]
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Reject any OOM entries
  const oomDates = entries
    .filter(e => !isDateInMonth(e.dateWorked, periodMonth))
    .map(e => e.dateWorked);
  if (oomDates.length > 0) {
    errors.push(`Entries outside ${periodMonth}: ${oomDates.join(', ')}`);
  }

  // Block only if rejected entries are mixed with approved/locked entries (partial approval state).
  // A fully-rejected month (all entries Rejected or Draft) is a valid resubmission.
  const hasRejected = entries.some(e => e.status === TimesheetStatus.Rejected);
  const hasApprovedOrLocked = entries.some(
    e => e.status === TimesheetStatus.ManagerApproved ||
         e.status === TimesheetStatus.SuperApproved ||
         e.status === TimesheetStatus.Locked
  );
  if (hasRejected && hasApprovedOrLocked) {
    errors.push('Contains rejected entries alongside approved entries — please contact your manager');
  }

  // Check each working non-holiday day has at least one entry (0 counts — part-time staff enter 0 for non-working days)
  const datesWithEntries = new Set(entries.map(e => e.dateWorked));
  const missingDays = allWorkingDays.filter(
    d => !bankHolidayDates.has(d) && !datesWithEntries.has(d)
  );
  if (missingDays.length > 0) {
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fmt = (d: string) => `${d.slice(8, 10)} ${MONTHS[parseInt(d.slice(5, 7), 10) - 1]} ${d.slice(0, 4)}`;
    errors.push(`Missing entries for ${missingDays.length} working day(s): ${missingDays.slice(0, 3).map(fmt).join(', ')}${missingDays.length > 3 ? ' …' : ''}`);
  }

  // Daily totals
  const dailyTotals = new Map<ISODateString, number>();
  for (const entry of entries) {
    dailyTotals.set(entry.dateWorked, (dailyTotals.get(entry.dateWorked) ?? 0) + entry.hours);
  }
  dailyTotals.forEach((total, date) => {
    if (total > VALIDATION.MAX_DAILY_HOURS) {
      errors.push(`${date}: ${total}h exceeds maximum ${VALIDATION.MAX_DAILY_HOURS}h`);
    } else if (total > VALIDATION.OOM_WARNING_THRESHOLD) {
      warnings.push(`${date}: ${total}h (over 7.5h)`);
    }
  });

  return { valid: errors.length === 0, errors, warnings };
}

// ─── Text field validation ────────────────────────────────────────────────────

export function validateRejectionReason(reason: string): ValidationResult {
  return reason.trim().length >= VALIDATION.MIN_REJECTION_REASON_LENGTH
    ? OK
    : fail([`Minimum ${VALIDATION.MIN_REJECTION_REASON_LENGTH} characters required`]);
}

export function validateUnlockReason(reason: string): ValidationResult {
  return reason.trim().length >= VALIDATION.MIN_UNLOCK_REASON_LENGTH
    ? OK
    : fail([`Minimum ${VALIDATION.MIN_UNLOCK_REASON_LENGTH} characters required`]);
}
